import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	Radio,
	RadioGroup,
	Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IGiftItem, IUpdateGiftPayload } from "./type";

interface Props {
	open: boolean;
	handleClose: () => void;
	gift: IGiftItem | null;
}

const UpdateGiftModal: React.FC<Props> = ({ open, handleClose, gift }) => {
	const [status, setStatus] = useState<number>(0);

	useEffect(() => {
		if (gift) {
			const isCollected =
				gift.gift_collected === 1 ||
				gift.gift_collected === true ||
				String(gift.gift_collected) === "1";
			setStatus(isCollected ? 1 : 0);
		}
	}, [gift]);

	const giftId = gift?.id;

	const { mutate: updateGift, isPending } = useApiMutation<
		IUpdateGiftPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/v1/admin/gift/${giftId || 0}`,
		invalidateQueries: ["Gift"],
	});

	const handleSubmit = () => {
		if (!giftId) return;

		const payload: IUpdateGiftPayload = {
			gift_collected: Number(status),
		};

		updateGift(
			{
				...payload,
				dynamicEndpoint: `/api/v1/admin/gift/${giftId}`,
			} as any,
			{
				onSuccess: () => {
					handleClose();
				},
			}
		);
	};

	const onDialogClose = () => {
		if (isPending) return;
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="xs"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					m: 0,
					p: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<EditTwoToneIcon color="primary" fontSize="small" />
					<Typography variant="h6" fontWeight={700}>
						Update Gift Status
					</Typography>
				</Box>
				<IconButton
					aria-label="close"
					onClick={onDialogClose}
					disabled={isPending}
					size="small"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ p: 2.5 }}>
				{gift && (
					<Box sx={{ mb: 2 }}>
						<Typography variant="body2" color="text.secondary">
							{`Visitor: ${gift.visitor_name || ""} (${gift.visitor_no || ""})`}
						</Typography>
					</Box>
				)}

				<FormControl component="fieldset" fullWidth>
					<FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
						Collection Status
					</FormLabel>
					<RadioGroup
						value={String(status)}
						onChange={(e) => setStatus(Number(e.target.value))}
					>
						<FormControlLabel
							value="1"
							control={<Radio color="success" size="small" />}
							label="Collected"
						/>
						<FormControlLabel
							value="0"
							control={<Radio color="warning" size="small" />}
							label="Not Collected"
						/>
					</RadioGroup>
				</FormControl>
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button
					onClick={onDialogClose}
					disabled={isPending}
					variant="outlined"
					color="inherit"
				>
					<span>Cancel</span>
				</Button>
				<LoadingButton
					onClick={handleSubmit}
					variant="contained"
					color="primary"
					loading={isPending}
				>
					<span>Update</span>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default UpdateGiftModal;
